import { createSlice } from '@reduxjs/toolkit';
import {
  getProfile,
  getHistory,
  getHistoryFilters,
  getUserPermissions,
  refreshUserPermissions,
  updateUserPermissions,
  updateProfile,
  getEditOptions,
  refreshProfile,
  uploadProfilePicture,
  deleteProfilePicture,
  setUserActive,
} from './services';
import initialState from './initialState';
import {
  getPanelValue,
  setPanelValue,
  toPermissionsChangeEntries,
} from './utils';

const isLatestRequest = (state, action) =>
  state.profile.requestId === action.meta.requestId;

const isPermissionsUser = (state, action) =>
  Boolean(state.permissions.panel) &&
  String(state.permissions.userId) === String(action.meta.arg.userId);

// Saves are serialized, so while a later save of the same key is still
// pending it decides the final value and an earlier failure must not roll
// it back.
const settlePermissionsSave = (state, action, succeeded) => {
  if (!isPermissionsUser(state, action)) return;
  const { panel, saves } = state.permissions;

  toPermissionsChangeEntries(action.meta.arg).forEach(([key, value]) => {
    const save = saves[key];
    if (!save) return;
    save.pending -= 1;
    if (succeeded) save.confirmed = value;
    if (save.pending > 0) return;
    if (!succeeded) setPanelValue(panel, key, save.confirmed);
    delete saves[key];
  });
};

const UserProfileSlice = createSlice({
  name: 'UserProfileSlice',
  initialState,
  reducers: {
    setHistoryFilter(state, action) {
      state.history.filterId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfile.pending, (state, action) => {
        state.profile.data = null;
        state.profile.loading = true;
        state.profile.error = false;
        state.profile.requestId = action.meta.requestId;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        if (!isLatestRequest(state, action)) return;
        state.profile.data = action.payload;
        state.profile.loading = false;
      })
      .addCase(getProfile.rejected, (state, action) => {
        if (!isLatestRequest(state, action)) return;
        state.profile.loading = false;
        state.profile.error = true;
      })
      .addCase(refreshProfile.fulfilled, (state, action) => {
        if (state.profile.data?.id !== action.meta.arg) return;
        state.profile.data = action.payload;
      })
      .addCase(updateProfile.pending, (state) => {
        state.profile.pendingSaves += 1;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.profile.pendingSaves -= 1;
        if (state.profile.data?.id !== action.meta.arg.userId) return;
        state.profile.data = action.payload;
      })
      .addCase(updateProfile.rejected, (state) => {
        state.profile.pendingSaves -= 1;
      })
      .addCase(uploadProfilePicture.fulfilled, (state, action) => {
        if (state.profile.data?.id !== action.meta.arg.userId) return;
        state.profile.data.picture = action.payload.profile_picture_url;
      })
      // The response carries the default avatar URL; null shows the empty state,
      // as the profile endpoint does for a user without a picture.
      .addCase(deleteProfilePicture.fulfilled, (state, action) => {
        if (state.profile.data?.id !== action.meta.arg.userId) return;
        state.profile.data.picture = null;
      })
      .addCase(setUserActive.fulfilled, (state, action) => {
        if (state.profile.data?.id !== action.meta.arg.userId) return;
        state.profile.data.is_active = action.meta.arg.isActive;
      })
      .addCase(getEditOptions.pending, (state) => {
        state.editOptions.loading = true;
        state.editOptions.teamsFailed = false;
      })
      .addCase(getEditOptions.fulfilled, (state, action) => {
        const { branches, teams } = action.payload;
        state.editOptions.loading = false;
        state.editOptions.teamsFailed = !teams;
        if (branches) state.editOptions.branches = branches;
        if (teams) state.editOptions.teams = teams;
      })
      .addCase(getEditOptions.rejected, (state) => {
        state.editOptions.loading = false;
        state.editOptions.teamsFailed = true;
      })
      .addCase(getUserPermissions.pending, (state, action) => {
        state.permissions.userId = action.meta.arg;
        state.permissions.panel = null;
        state.permissions.loading = true;
        state.permissions.error = null;
        state.permissions.requestId = action.meta.requestId;
        state.permissions.saves = {};
      })
      .addCase(getUserPermissions.fulfilled, (state, action) => {
        if (state.permissions.requestId !== action.meta.requestId) return;
        state.permissions.panel = action.payload;
        state.permissions.loading = false;
      })
      .addCase(getUserPermissions.rejected, (state, action) => {
        if (state.permissions.requestId !== action.meta.requestId) return;
        state.permissions.loading = false;
        state.permissions.error = action.payload ?? 'unknown';
      })
      // A save started after the refresh went out would be overwritten by an
      // older panel, so the refresh only lands when nothing is in flight.
      .addCase(refreshUserPermissions.fulfilled, (state, action) => {
        const { userId, panel, saves } = state.permissions;
        if (!panel || String(userId) !== String(action.meta.arg)) return;
        if (Object.keys(saves).length) return;
        state.permissions.panel = action.payload;
      })
      .addCase(updateUserPermissions.pending, (state, action) => {
        if (!isPermissionsUser(state, action)) return;
        const { panel, saves } = state.permissions;

        toPermissionsChangeEntries(action.meta.arg).forEach(([key, value]) => {
          if (!saves[key]) {
            saves[key] = { pending: 0, confirmed: getPanelValue(panel, key) };
          }
          saves[key].pending += 1;
          setPanelValue(panel, key, value);
        });
      })
      .addCase(updateUserPermissions.fulfilled, (state, action) =>
        settlePermissionsSave(state, action, true)
      )
      .addCase(updateUserPermissions.rejected, (state, action) =>
        settlePermissionsSave(state, action, false)
      )
      .addCase(getHistoryFilters.fulfilled, (state, action) => {
        state.history.filters = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(getHistory.pending, (state, action) => {
        const isFirstPage = !action.meta.arg.cursor;
        state.history.userId = action.meta.arg.userId;
        if (isFirstPage) {
          state.history.events = [];
          state.history.nextCursor = null;
        }
        state.history.loading = isFirstPage;
        state.history.loadingMore = !isFirstPage;
        state.history.error = null;
        state.history.requestId = action.meta.requestId;
      })
      .addCase(getHistory.fulfilled, (state, action) => {
        if (state.history.requestId !== action.meta.requestId) return;
        state.history.events.push(...(action.payload?.results ?? []));
        state.history.nextCursor = action.payload?.next_cursor ?? null;
        state.history.loading = false;
        state.history.loadingMore = false;
      })
      .addCase(getHistory.rejected, (state, action) => {
        if (state.history.requestId !== action.meta.requestId) return;
        state.history.loading = false;
        state.history.loadingMore = false;
        state.history.error = action.payload ?? 'unknown';
      });
  },
});

export const { setHistoryFilter } = UserProfileSlice.actions;
export default UserProfileSlice.reducer;
